import './indication-details-view.css'

import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined'
import ArrowsAltOutlined from '@ant-design/icons/ArrowsAltOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import WarningTwoTone from '@ant-design/icons/WarningTwoTone'
import {
  Button,
  Card,
  ConfigProvider,
  Drawer,
  Modal,
  Spin,
  Tabs,
  Typography,
} from 'antd'
import {
  IndicationCardsSwr,
  IndicationDetailCardExtensionContext,
  IndicationDetailCardExtensionProvider,
  IndicationDetailCardGroup,
  IndicationDetailCardLeaf,
  IndicationItem,
  LoadedIndicationDetailCard,
  useIndicationCards,
  useIndicationData,
} from 'features/indicationTriage'
import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'

const { Text } = Typography

const LeafContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`

const IndicationDetailCardsContext = createContext<IndicationCardsSwr>({
  cardComponents: new Map(),
  isLoading: false,
})

export interface ToggledIndicationDetailViewProps extends IndicationDetailViewProps {
  isOpen: boolean
  close: () => void
  type?: 'drawer' | 'modal'
}

// This jusr wraps `IndicationDetailView` in a MOdal and wires up some props.
const RawToggledIndicationDetailView: FC<ToggledIndicationDetailViewProps> = (
  props
) => {
  const title = useMemo(
    () => <Text strong>Details: {props.indicationItem?.title}</Text>,
    [props.indicationItem?.title]
  )
  return !props.type || props.type === 'modal' ? (
    <Modal
      open={props.isOpen}
      onCancel={props.close}
      footer={null}
      width='80%'
      title={title}>
      <IndicationDetailView OverrideDetailCard={ModalCard} {...props} />
    </Modal>
  ) : (
    <Drawer
      title={title}
      placement='right'
      closable={true}
      size='large'
      styles={{ wrapper: { width: '100%' } }}
      onClose={props.close}
      getContainer={false}
      open={props.isOpen}>
      <IndicationDetailView OverrideDetailCard={DrawerCard} {...props} />
    </Drawer>
  )
}

export const ToggledIndicationDetailView = React.memo(
  RawToggledIndicationDetailView
)

export interface IndicationDetailViewProps {
  indicationItem: IndicationItem | null
  cardConfig: ReadonlyArray<
    IndicationDetailCardGroup | IndicationDetailCardLeaf
  >
  OverrideDetailCard?: FC<IndicationDetailDisplayCardProps>
  extensionContext?: IndicationDetailCardExtensionContext
}

const IndicationDetailViewContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  overflow: wrap;
`

// This is hte main component for the detail cards - it loads the configured cards,
// and renders them according to the config.
export const IndicationDetailView: FC<IndicationDetailViewProps> = (props) => {
  const { cardConfig, OverrideDetailCard, indicationItem, extensionContext } =
    props
  const cardSwr = useIndicationCards(cardConfig)

  return (
    <IndicationDetailViewContainer>
      <IndicationDetailCardsContext.Provider value={cardSwr}>
        <IndicationDetailCardExtensionProvider value={extensionContext}>
          {indicationItem && (
            <IndicationDetailCardsView
              cards={cardConfig}
              OverrideDetailCard={OverrideDetailCard}
            />
          )}
        </IndicationDetailCardExtensionProvider>
      </IndicationDetailCardsContext.Provider>
    </IndicationDetailViewContainer>
  )
}

const WarnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: 1rem;
`

interface IndicationDetailDisplayCardProps {
  title: ReactNode
  children: ReactNode
  style?: React.CSSProperties
  focusCard?: () => void
}

// If we fail to load a card for some reason, we show this in its place
// so an FDE knows something went wrong.
const WarnCard: React.FC<{ id: string }> = ({ id }) => {
  const { theme } = useGeckoTheme()
  return (
    <WarnContainer>
      <WarningTwoTone
        twoToneColor={theme.colors.BrightOrange}
        style={{ fontSize: '72px' }}
      />
      <Text>Unable to load card with ID: {id}</Text>
    </WarnContainer>
  )
}

const DrawerCard: React.FC<IndicationDetailDisplayCardProps> = ({
  children,
}) => {
  return <div style={{ width: '100%', marginBottom: '1rem' }}>{children}</div>
}

const ModalCard: React.FC<IndicationDetailDisplayCardProps> = ({
  title,
  children,
  style,
  focusCard,
}) => {
  return (
    <Card
      style={style}
      title={title}
      extra={
        focusCard ? (
          <Button shape='circle' onClick={focusCard}>
            <ArrowsAltOutlined />
          </Button>
        ) : undefined
      }>
      {children}
    </Card>
  )
}

const DEFAULT_CARD_WIDTH = '24rem'

interface IndicationDetailCardsViewProps {
  cards: ReadonlyArray<IndicationDetailCardGroup | IndicationDetailCardLeaf>
  OverrideDetailCard?: FC<IndicationDetailDisplayCardProps>
}

const CardWrapper: FC<{
  children: ReactNode
  useIsEnabled: LoadedIndicationDetailCard['useIsEnabled']
}> = ({ useIsEnabled, children }) => {
  const { indication } = useIndicationData()
  const enabled = useIsEnabled?.(indication) ?? true
  return enabled ? <>{children}</> : <></>
}

const FocusContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
  justify-content: center;
  align-items: center;
`

const FocusBackContainer = styled.div`
  display: flex;
  width: 90%;
  justify-content: start;
`

/**
 * This renders each of the cards - see the README for indication_triage.
 * For every `IndicationDetailCardLeaf`, it renders a card, and for a
 * `IndicationDetailCardGroup`, it renders a `Tabs`, populated with
 *  its children (which can be either cards or more groups).
 */
const IndicationDetailCardsView: FC<IndicationDetailCardsViewProps> = ({
  cards,
  OverrideDetailCard,
}) => {
  const { cardComponents, isLoading } = useContext(IndicationDetailCardsContext)
  const leaves: IndicationDetailCardLeaf[] = useMemo(
    () => cards.filter((card) => 'id' in card),
    [cards]
  )
  const groups: IndicationDetailCardGroup[] = useMemo(
    () => cards.filter((card) => 'children' in card),
    [cards]
  )
  const DetailCardComponent = useMemo(
    () => OverrideDetailCard ?? Card,
    [OverrideDetailCard]
  )

  const { theme } = useGeckoTheme()
  const { indication } = useIndicationData()

  const hasOneLeafCard = useMemo(
    () => cards.length === 1 && leaves.length === 1,
    [cards.length, leaves.length]
  )

  const [focusLeafId, setFocusLeafId] = useState<string | null>(
    hasOneLeafCard ? leaves[0].id : null
  )
  const { focusLeaf, focusCard } = useMemo(() => {
    const focusLeaf = leaves.find((l) => l.id === focusLeafId)
    if (!focusLeaf) {
      return {
        focusLeaf: undefined,
        focusCard: undefined,
      }
    }
    const focusCard = cardComponents.get(focusLeaf.id)
    return { focusLeaf, focusCard }
  }, [cardComponents, focusLeafId, leaves])

  const { Component: FocusCardComponent } =
    focusCard && focusCard !== 'Error' ? focusCard : { Component: undefined }

  return (
    <ConfigProvider
      theme={{
        components: {
          Card: {
            colorBgContainer: theme.colors.grayscale[600],
          },
          Tabs: {
            lineType: 'transparent',
          },
        },
      }}>
      <Spin
        spinning={isLoading}
        style={{ width: '100%' }}
        wrapperClassName='modal-content'>
        {focusLeaf && focusCard && FocusCardComponent ? (
          <FocusContainer>
            <FocusBackContainer>
              <Button onClick={() => setFocusLeafId(null)} size='small'>
                <ArrowLeftOutlined /> Back
              </Button>
            </FocusBackContainer>
            <FocusCardComponent config={focusLeaf.config ?? {}} />
          </FocusContainer>
        ) : (
          <LeafContainer>
            {leaves.map((leaf) => {
              const loadedCard = cardComponents.get(leaf.id)
              if (loadedCard === 'Error') {
                return <WarnCard id={leaf.id} key={leaf.id} />
              } else if (loadedCard) {
                const { Component, title, width } = loadedCard
                return (
                  <CardWrapper
                    useIsEnabled={loadedCard.useIsEnabled}
                    key={leaf.id}>
                    <DetailCardComponent
                      style={{
                        width: width ?? DEFAULT_CARD_WIDTH,
                        height: '24rem',
                        overflow: 'auto',
                      }}
                      title={
                        indication ? title?.(indication) : 'Unknown indication'
                      }
                      focusCard={() => setFocusLeafId(leaf.id)}>
                      <React.Suspense
                        fallback={<Spin indicator={<LoadingOutlined />} />}>
                        <Component config={leaf.config ?? {}} />
                      </React.Suspense>
                    </DetailCardComponent>
                  </CardWrapper>
                )
              }
            })}
          </LeafContainer>
        )}
        <Tabs
          style={{ width: '100%' }}
          items={groups.map((cardGroup) => {
            return {
              key: cardGroup.title,
              label: cardGroup.title,
              children: (
                <IndicationDetailCardsView
                  cards={cardGroup.children}
                  OverrideDetailCard={OverrideDetailCard}
                />
              ),
            }
          })}
        />
      </Spin>
    </ConfigProvider>
  )
}
